import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CaseDetails, LeaveInfoDialog, LeaveInfo, PostLeaveInfo, StiiraError, PostLeaveInfoForm } from '@core/models';
import { ErrorService, LayoutService } from '@core/services';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Subject, Observable } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { LeaveStages } from '@core/enums/leave-stages.enum';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';
import { IneligibilityReasons, LeaveEligibilities } from '@core/enums/leave-eligibilities.enum';
import { takeUntil } from 'rxjs/operators';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { LeaveInfoActionItemsTemplatesComponent } from '../leave-info-action-item-templates/leave-info-action-item-templates.component';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';

@Component({
    selector: 'app-leave-info-dialog',
    templateUrl: './leave-info-dialog.component.html',
    styleUrls: ['./leave-info-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeaveInfoDialogComponent implements OnInit {
  static readonly componentName = 'LeaveInfoDialogComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public form: FormGroup<PostLeaveInfoForm>;
  public categoryHint: string = null;
  public intermittentFreqHint: string = null;
  public isAnonymous: boolean = false;
  public leaveStages = LeaveStages;

  public position = position;
  public onDragEnded = onDragEnded;
  
  private formInitValues: any;
  private formChangeEmitted: boolean = false;
  private leaveInfo: LeaveInfo;
  private destroy$: Subject<void> = new Subject<void>();

  public isMobile$: Observable<boolean>;

  get caseId(): FormControl<number> {
    return this.form.controls.caseId;
  }  

  get stage(): FormControl<string> {
    return this.form.controls.stage;
  }  

  get dateReceived(): FormControl<Date> {
    return this.form.controls.dateReceived;
  }  

  get requestedStartDate(): FormControl<Date> {
    return this.form.controls.requestedStartDate;
  }  

  get requestedEndDate(): FormControl<Date> {
    return this.form.controls.requestedEndDate;
  }  

  get leaveReason(): FormControl<number> {
    return this.form.controls.leaveReasonId;
  }  
  
  get twelveMonthsHours(): FormControl<number> {
    return this.form.controls.twelveMonthsHours;
  }

  get twelveMonthsDate(): FormControl<Date> {
    return this.form.controls.twelveMonthsDate;
  }

  get twelveMonthsComments(): FormControl<string> {
    return this.form.controls.twelveMonthsComments;
  }

  get averageWeeklyHours(): FormControl<number> {
    return this.form.controls.averageWeeklyHours;
  }

  get averageWeeklyHoursDate(): FormControl<Date> {
    return this.form.controls.averageWeeklyHoursDate;
  }

  get averageWeeklyHoursComments(): FormControl<string> {
    return this.form.controls.averageWeeklyHoursComments;
  }

  get averageWeeklyWorkdays(): FormControl<number> {
    return this.form.controls.averageWeeklyWorkdays;
  }

  get averageWeeklyWorkdaysDate(): FormControl<Date> {
    return this.form.controls.averageWeeklyWorkdaysDate;
  }

  get averageWeeklyWorkdaysComments(): FormControl<string> {
    return this.form.controls.averageWeeklyWorkdaysComments;
  }

  get startDate(): FormControl<Date> {
    return this.form.controls.startDate;
  }

  get leaveType(): FormControl<number> {
    return this.form.controls.leaveType;
  } 

  get leaveSubtype(): FormControl<string> {
    return this.form.controls.leaveSubtype;
  }

  get endDate(): FormControl<Date> {
    return this.form.controls.endDate;
  }

  get leaveCategory(): FormControl<string> {
    return this.form.controls.leaveCategory;
  }

  get intermittentFreq(): FormControl<string> {
    return this.form.controls.intermittentFreq;
  }

  get relatedCases(): FormControl<number[]> {
    return this.form.controls.relatedCases;
  }

  get eligibility(): FormControl<number> {
    return this.form.controls.eligibility;
  }

  get ineligibilityReasonId(): FormControl<number> {
    return this.form.controls.ineligibilityReasonId;
  }

  get ineligibilityReasonComments(): FormControl<string> {
    return this.form.controls.ineligibilityReasonComments;
  }

  get importVerified(): FormControl<boolean> {
    return this.form.controls.importVerified;
  }

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      caseData: CaseDetails;
      sysText: any;
      updateConfSysText: any
      options: LeaveInfoDialog;
      fieldNotifications: { [key: string]: string };
    },
    private fb: FormBuilder,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private leaveInfoDialogRef: MatDialogRef<LeaveInfoDialogComponent>,
    private leaveService: LeaveAdminService,
    private store: LeaveAdminStoreService,
    private layoutService: LayoutService,
  ) { 
    this.form = this.fb.group<PostLeaveInfoForm>({
      caseId: this.fb.control(null),
      stage: this.fb.control(null), 
      dateReceived: this.fb.control(null),
      requestedStartDate: this.fb.control(null),
      requestedEndDate: this.fb.control(null),
      leaveReasonId: this.fb.control(null),

      twelveMonthsHours: this.fb.control(null),
      twelveMonthsDate: this.fb.control(null),
      twelveMonthsComments: this.fb.control(null),
      averageWeeklyHours: this.fb.control(null),
      averageWeeklyHoursDate: this.fb.control(null),
      averageWeeklyHoursComments: this.fb.control(null),
      averageWeeklyWorkdays: this.fb.control(null),
      averageWeeklyWorkdaysDate: this.fb.control(null),
      averageWeeklyWorkdaysComments: this.fb.control(null),

      startDate: this.fb.control(null),
      leaveType: this.fb.control(null),
      leaveSubtype: this.fb.control(null),
      endDate: this.fb.control(null),
      leaveCategory: this.fb.control(null),
      intermittentFreq: this.fb.control(null),
      relatedCases: this.fb.control(null),
      eligibility: this.fb.control(null),
      ineligibilityReasonId: this.fb.control(null),
      ineligibilityReasonComments: this.fb.control(null),

      importVerified: this.fb.control(null)
    });
  }
  
  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.leaveInfoDialogRef, this.isSubmitting$, () => this.close());

    this.leaveInfo = this.data.caseData.leaveInformation;
    this.setupForm();
    
    this.isAnonymous = this.data.caseData.employeeInformation.isAnonymous;

    this.patchRelatedCases();

    this.leaveType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((typeId: number)=>{
        this.handleLeaveTypeChange(typeId);
      });

    this.eligibility.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.handleEligibilityChange();
      });

    this.ineligibilityReasonId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.handleIneligibilityReasonChange();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setupForm() {
    this.form.patchValue({
      ...this.leaveInfo,
      caseId: this.leaveInfo?.caseId,
      stage: this.currentStageName(),
      dateReceived: this.leaveInfo?.dateReceived,
      requestedStartDate: this.leaveInfo?.requestedStartDate,
      requestedEndDate: this.leaveInfo?.requestedEndDate,
      startDate: this.leaveInfo?.startDate,
      endDate: this.leaveInfo?.endDate,
      leaveReasonId: this.leaveInfo?.leaveReason?.id as number,

      twelveMonthsHours: this.leaveInfo?.twelveMonthsHours,
      twelveMonthsDate: this.leaveInfo?.twelveMonthsDate,
      twelveMonthsComments: this.leaveInfo?.twelveMonthsComments,
      averageWeeklyHours: this.leaveInfo?.averageWeeklyHours,
      averageWeeklyHoursDate: this.leaveInfo?.averageWeeklyHoursDate,
      averageWeeklyHoursComments: this.leaveInfo?.averageWeeklyHoursComments,
      averageWeeklyWorkdays: this.leaveInfo?.averageWeeklyWorkdays,
      averageWeeklyWorkdaysDate: this.leaveInfo?.averageWeeklyWorkdaysDate,
      averageWeeklyWorkdaysComments: this.leaveInfo?.averageWeeklyWorkdaysComments,

      intermittentFreq: this.leaveInfo?.intermittentFreq,
      eligibility: this.leaveInfo?.eligibility?.id as number,
      relatedCases: this.leaveInfo?.relatedCases.map(({ caseId }) => caseId),
      leaveType: this.leaveInfo?.leaveType?.id as number,
      leaveSubtype: this.leaveInfo.leaveSubtype,
      leaveCategory: this.leaveInfo?.category?.description,
      ineligibilityReasonId: <number>this.leaveInfo?.ineligibilityReason?.id,
      ineligibilityReasonComments: this.leaveInfo?.ineligibilityReasonComments,

      importVerified: this.leaveInfo?.caseImportInfo?.importVerified
    });
    
    this.caseId.disable();
    this.stage.disable();
    this.leaveCategory.disable();
    
    if (this.data.options.relatedCases.length === 0) {
      this.relatedCases.disable();
    }
    
    if (this.leaveInfo.leaveType) {
      const isIntermittent = this.data.options.leaveTypes.filter(lt => lt.id === this.leaveInfo.leaveType.id)[0].isIntermittent;
      this.setIntermittentFreqField(isIntermittent);
    } else {
      this.leaveSubtype.disable();
    }

    if (this.leaveInfo.eligibility?.id !== LeaveEligibilities.Ineligible) {
      this.ineligibilityReasonId.disable();
      this.ineligibilityReasonComments.disable();
    }

    if (this.leaveInfo.eligibility?.id === LeaveEligibilities.Ineligible) {
      this.ineligibilityReasonId.setValidators(Validators.required);
    }

    if (this.leaveInfo.ineligibilityReason?.id === IneligibilityReasons.Other) {
      this.ineligibilityReasonComments.setValidators(Validators.required);
    }
    
    setTimeout(() => {
      this.errorService.setFormModelStateErrors(this.form, this.data.caseData.missingDetails)
      this.formInitValues = { ...this.form.value }
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  private currentStageName(): string {
    var stageName = this.data.caseData.stages != null 
      ? this.data.caseData.stages
        .filter(s => s.stageId === this.leaveInfo?.currentStageId)[0].stageName 
      : null;

    return stageName;
  }

  public onSubmit(
    overrideUpdateConf: boolean = false,
    overrideOverdraw: boolean = false): void {
    this.form.markAsUntouched();
    if (this.store.isActionRunning) {
      this.openPendingChangesDialog(overrideUpdateConf, overrideOverdraw);
      return;
    }
    // if leave type or reason has changed show action item template dialog
    if (this.leaveType.value != this.formInitValues.leaveType 
      || this.leaveReason.value != this.formInitValues.leaveReasonId) {
      this.openActionItemsTemplateDialog(overrideUpdateConf, overrideOverdraw);
    } else {
      this.postSaveInfo(overrideUpdateConf, overrideOverdraw);
    }
  }

  private postSaveInfo(
    overrideUpdateConf: boolean = false,
    overrideOverdraw: boolean = false,
    actionItemsTemplateId: number = null): void {
    if (this.store.isActionRunning) {
      this.openPendingChangesDialog(overrideUpdateConf, overrideOverdraw);
      return;
    }

    const dto: PostLeaveInfo = {
      overrideUpdateConf: overrideUpdateConf,
      overrideOverdraw: overrideOverdraw,

      caseId: this.caseId.value,
      dateReceived: this.dateReceived.value,
      requestedStartDate: this.requestedStartDate.value,
      requestedEndDate: this.requestedEndDate.value,
      startDate: this.startDate.value,
      endDate: this.endDate.value,
      leaveReasonId: this.leaveReason.value,

      twelveMonthsHours: this.twelveMonthsHours.value,
      twelveMonthsDate: this.twelveMonthsDate.value,
      twelveMonthsComments: this.twelveMonthsComments.value,
      averageWeeklyHours: this.averageWeeklyHours.value,
      averageWeeklyHoursDate: this.averageWeeklyHoursDate.value,
      averageWeeklyHoursComments: this.averageWeeklyHoursComments.value,
      averageWeeklyWorkdays: this.averageWeeklyWorkdays.value,
      averageWeeklyWorkdaysDate: this.averageWeeklyWorkdaysDate.value,
      averageWeeklyWorkdaysComments: this.averageWeeklyWorkdaysComments.value,

      eligibilityId: this.eligibility.value,
      leaveTypeId: this.leaveType.value,
      leaveSubtype: this.leaveSubtype.value,
      ineligibilityReasonId: this.ineligibilityReasonId.value,
      intermittentFreq: this.intermittentFreq.value,
      relatedCases: this.form.value.relatedCases,
      ineligibilityReasonComments: this.ineligibilityReasonComments.value,

      importVerified: this.data.caseData.leaveInformation.caseImportInfo ? this.importVerified.value : null,

      actionItemsTemplateId: actionItemsTemplateId
    }

    this.leaveService.postLeaveInfo(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe(
        (res) => {
          if (res.showUpdateConfirmationDialog || res.showOverrideOverdrawDialog) {
            this.openUpdateConfirmationDialog(
              res as UpdateConfirmationDialog,
              overrideUpdateConf,
              overrideOverdraw,
              actionItemsTemplateId);
          }
          else {
            this.store.caseDetails = res as CaseDetails;
            this.leaveInfoDialogRef.close();
          }
        },
        (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
      );
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.sysText.unsavedChanges,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.leaveInfoDialogRef.close();
        }
      });
  }

  public close(): void {
    if (this.noChanges) {
      this.leaveInfoDialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  private patchRelatedCases() {
    let relatedCases: number[] = [];
    this.leaveInfo?.relatedCases?.forEach(rc => {
      relatedCases.push(rc.caseId)
    })
    this.form.controls.relatedCases.patchValue(relatedCases)
  }

  private setIntermittentFreqField(isIntermittent: boolean): void {
    if (!isIntermittent) {
      this.intermittentFreq.disable();
      this.intermittentFreq.reset();
      this.intermittentFreqHint = this.data.sysText.caseLeaveInfoDialog.notApplicable;
    } else {
      this.intermittentFreq.enable();
      this.intermittentFreqHint = null;
    }
  }

  private handleLeaveTypeChange(typeId: number): void {
    if (!typeId) {
      this.leaveCategory.setValue(null);
      this.leaveSubtype.setValue(null);
      this.leaveSubtype.disable();
    } else {
      this.leaveSubtype.enable();
      const leaveType = this.data.options.leaveTypes.filter(lt => lt.id === typeId)[0];
      const catName = this.data.options.leaveCategories.filter(lc => lc.id === leaveType.categoryId)[0].description;
      this.leaveCategory.setValue(catName);
      this.setIntermittentFreqField(leaveType.isIntermittent);
      if (this.leaveInfo.category != null && leaveType.categoryId != this.leaveInfo.category?.id) {
        const hintText =  this.data.sysText.caseLeaveInfoDialog.categoryHint;
        const capHint = hintText[0].toUpperCase() + hintText.slice(1);
        this.categoryHint = capHint.replace('@[category]', catName);
      } else {
        this.categoryHint = null;
      }
    }
  }

  private handleEligibilityChange(): void {
    if (this.eligibility.value !== LeaveEligibilities.Ineligible) {
      this.ineligibilityReasonId.removeValidators(Validators.required)
      this.ineligibilityReasonComments.removeValidators(Validators.required)
      this.ineligibilityReasonId.setValue(null);
      this.ineligibilityReasonComments.setValue(null);
      this.ineligibilityReasonId.disable();
      this.ineligibilityReasonComments.disable();
    }
    else {
      this.ineligibilityReasonId.enable();
      this.ineligibilityReasonComments.enable();
      this.ineligibilityReasonId.setValidators(Validators.required);
      this.handleIneligibilityReasonChange();
    }
    this.form.updateValueAndValidity();
    this.form.markAsTouched();
  }

  private handleIneligibilityReasonChange(): void {
      if (this.ineligibilityReasonId.value == IneligibilityReasons.Other) {
        this.ineligibilityReasonComments.setValidators(Validators.required);
      }
      else {
        this.ineligibilityReasonComments.removeValidators(Validators.required);
      }
      this.form.updateValueAndValidity();
      this.form.markAsTouched();
  }

  private openUpdateConfirmationDialog(
    updateConfirmationDialog: UpdateConfirmationDialog,
    overrideUpdateConf: boolean,
    overrideOverdraw: boolean,
    actionItemsTemplateId: number = null): void {
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.data.updateConfSysText,
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed()
      .subscribe(
        (res: boolean) => {
          if (updateConfirmationDialog.showUpdateConfirmationDialog)
            overrideUpdateConf = res;
          else if (updateConfirmationDialog.showOverrideOverdrawDialog)
            overrideOverdraw = res;

          if (res) {
            this.postSaveInfo(overrideUpdateConf, overrideOverdraw, actionItemsTemplateId);
          }
        });
  }

  private openActionItemsTemplateDialog(
    overrideUpdateConf: boolean,
    overrideOverdraw: boolean): void {
    this.leaveService.getLeaveInfoActionItemsTemplatesDialog({caseId: this.leaveInfo.caseId, leaveTypeId: this.leaveType.value})
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res) => {
        const dialogConfig: MatDialogConfig = {
          id: LeaveInfoActionItemsTemplatesComponent.componentName,
          width: '500px',
          data: {
            options: res.templateOptions,
            sysText: this.store.sysText.leaveInfoActionItemsTemplatesDialog,
          }
        };
        this.dialog.open(LeaveInfoActionItemsTemplatesComponent, dialogConfig)
          .beforeClosed().subscribe((res: {templateId: number, saved: boolean}) => {
            if (res.saved) {
              this.postSaveInfo(overrideUpdateConf, overrideOverdraw, res.templateId);
            }
          });
      });
  }

  private openPendingChangesDialog(
    overrideUpdateConf: boolean,
    overrideOverdraw: boolean): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px',
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSubmit(overrideUpdateConf, overrideOverdraw);
      }
    });
  }
}